"use client";

import { useSuspenseQuery } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { defaultTo } from "lodash-es";
import { useCallback, useMemo } from "react";
import { useLocale } from "use-intl";

import { useLists } from "@/contexts/lists-context";
import { useVideoPlayer } from "@/contexts/video-player-context";
import { useDeferredResourceQuery } from "@/hooks/use-deferred-resource";
import { useRecordView } from "@/hooks/use-record-view";
import {
  useAutoPlayFromSearchParam,
  useSyncPlayingTrackSearchParam,
} from "@/hooks/use-track-playback-search-params";
import { useTrackVideoPlayerControls } from "@/hooks/use-track-video-player-controls";
import { useTrackVideoPlaylist } from "@/hooks/use-track-video-playlist";
import { getReleaseNotesResourceQueryOptions } from "@/routes/_app/release/$id/release-notes-resource-query";
import { getReleaseQueryOptions } from "@/routes/_app/release/$id/release-query";
import { getReleaseTrackVideosQueryOptions } from "@/routes/_app/release/$id/release-track-videos-query";
import { orpc } from "@/utils/orpc.client";

// Use getRouteApi (not the route import) to avoid circular deps between the
// route file and this hook.
const routeApi = getRouteApi("/_app/release/$id");

export function useReleaseContentController({ id }: { id: string }) {
  const locale = useLocale();
  const { currentTrack } = useVideoPlayer();
  const { isInCollection } = useLists();
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const playing = search.playing ?? null;
  const setPlaying = useCallback(
    (value: string | null) => {
      navigate({
        search: (prev) => ({ ...prev, playing: value ?? undefined }),
        replace: false,
        resetScroll: false,
      });
    },
    [navigate],
  );

  const releaseQuery = useSuspenseQuery(getReleaseQueryOptions({ id, locale }));
  const computeReleaseNotesResource = useCallback(
    () => orpc.music.computeReleaseNotesResource.call({ releaseId: id, locale }),
    [id, locale],
  );
  const { value: notes } = useDeferredResourceQuery({
    queryOptions: getReleaseNotesResourceQueryOptions({
      releaseId: id,
      locale,
    }),
    compute: computeReleaseNotesResource,
  });

  const computeTrackVideosResource = useCallback(
    () => orpc.music.computeTrackVideosResource.call({ entity: "release", id }),
    [id],
  );
  const {
    query: trackVideosQuery,
    resource: trackVideosResource,
    value: trackVideosValue,
  } = useDeferredResourceQuery({
    queryOptions: getReleaseTrackVideosQueryOptions(id),
    compute: computeTrackVideosResource,
  });

  useRecordView("work", releaseQuery.data?.workId ?? undefined);

  const baseRelease = releaseQuery.data!;
  const release = useMemo(
    () =>
      notes
        ? {
            ...baseRelease,
            notes: notes.notes,
            notesHtml: notes.notesHtml,
          }
        : baseRelease,
    [baseRelease, notes],
  );
  const releaseMedia = defaultTo(release.media, []);
  const trackVideos = defaultTo(trackVideosValue, []);
  const isPlayAllResolving = Boolean(
    trackVideosQuery.isLoading || trackVideosResource?.status === "pending",
  );
  const videoPlaylist = useTrackVideoPlaylist(releaseMedia, trackVideos);
  const artistName = defaultTo(releaseQuery.data?.primaryArtistName, null);
  const releaseSource = useMemo(
    () => (release ? { title: release.title, url: `/release/${id}` } : null),
    [release?.title, id],
  );
  const {
    playAll: handlePlayAll,
    playByYoutubeId: handlePlayTrack,
    hasPlayableTracks,
  } = useTrackVideoPlayerControls({
    playlist: videoPlaylist,
    artistName,
    source: releaseSource,
  });

  useSyncPlayingTrackSearchParam({
    playing,
    setPlaying,
    currentYoutubeId: currentTrack?.youtubeId,
  });

  useAutoPlayFromSearchParam({
    playing,
    playlist: videoPlaylist,
    onPlayByYoutubeId: handlePlayTrack,
  });

  return {
    release,
    releaseMedia,
    trackVideos,
    isInCollection,
    hasPlayableTracks,
    isPlayAllResolving,
    handlePlayAll,
  };
}
